/* eslint-disable prefer-regex-literals */
import isHotkey from "is-hotkey";
import Prism from "prismjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import type { NodeEntry } from "slate";
import { createEditor, Editor, Range, Text, Transforms } from "slate";
import { withHistory } from "slate-history";
import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import timecode from "smpte-timecode";
import styled from "styled-components";

import {
  dualInlineMatchers,
  dualNakedTimecodeMatchers,
  inlineMatchers,
  nakedTimecodeMatchers,
  topLevelMatchers,
} from "../../../lib/storyNotes";
import type { RootState } from "../../../store/state/rootReducer";
import { handleHotkey } from "../../../store/state/story_authoring/actions";
import {
  SetIndexNotes,
  SetSkipTime,
  ToggleAddBookmarkToNotes,
  ToggleAddChapterToNotes,
} from "../../../store/state/story_authoring/reducer";
import type { IndexChapter } from "../../../store/state/story_authoring/reducer";
import type { SlateParagraph } from "../../../components/utils/types";
import type { AppDispatch } from "../../../store/state/rootStore";

import Leaf from "./Leaf";

const NotesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  & {
    div[data-slate-editor] {
      font-family: source-code-pro, Monaco, Menlo, Consolas, "Courier New", monospace;
    }

    /* leafs */

    .leaf.bold {
      font-weight: bold;
    }

    .leaf.italic {
      font-style: italic;
    }

    .leaf.underlined {
      text-decoration: underline;
    }

    .leaf.title {
      display: inline-block;
      font-weight: bold;
      font-size: 20px;
      margin: 20px 0 10px 0;
    }

    .leaf.list {
      padding-left: 10px;
      font-size: 20px;
      line-height: 10px;
    }

    .leaf.hr {
      display: block;
      text-align: center;
      border-bottom: 2px solid #ddd;
    }

    .leaf.blockquote {
      display: inline-block;
      border-left: 2px solid #ddd;
      padding-left: 10px;
      font-size: 16px;
      margin-bottom: unset;
    }

    .leaf.code {
      font-family: monospace;
      background-color: #eee;
      padding: 3px;
      cursor: pointer;
    }

    .leaf.timecode {
      font-family: monospace;
      background-color: lightblue;
      padding: 3px;
      cursor: pointer;
    }

    .leaf.timecodesimple {
      font-family: monospace;
      background-color: lightpink;
      padding: 3px;
      cursor: pointer;
    }

    .leaf.timecoderow {
      border-top: 2px solid #ddd;
      font-family: monospace;
      display: inline-block;
      font-weight: bold;
      font-size: 20px;
      margin: 20px 0 0 0;
      cursor: pointer;
    }
  }
`;

Prism.languages.markdown = Prism.languages.extend("markup", {});
Prism.languages.insertBefore("markdown", "prolog", {
  blockquote: { pattern: /^>.*$/m, alias: "punctuation" },
  code: [
    { pattern: /^(?: {4}|\t).+/m, alias: "keyword" },
    { pattern: /``.+?``|`[^`\n]+`/, alias: "keyword" },
  ],
  title: [
    // {
    //   pattern: /\w+.*(?:\r?\n|\r)(?:==+|--+)/,
    //   alias: 'important',
    //   inside: { punctuation: /==+$|--+$/ },
    // },
    {
      pattern: /(^\s*)#+.+/m,
      lookbehind: !0,
      alias: "important",
      inside: { punctuation: /^#+|#+$/ },
    },
  ],
  hr: {
    pattern: /(^\s*)([*-])([\t ]*\2){2,}(?=\s*$)/m,
    lookbehind: !0,
    alias: "punctuation",
  },
  list: {
    pattern: /(^\s*)(?:[*+-]|\d+\.)(?=[\t ].)/m,
    lookbehind: !0,
    alias: "punctuation",
  },
  // 'url-reference': {
  //   pattern: /!?\[[^\]]+\]:[\t ]+(?:\S+|<(?:\\.|[^>\\])+>)(?:[\t ]+(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\((?:\\.|[^)\\])*\)))?/,
  //   inside: {
  //     variable: { pattern: /^(!?\[)[^\]]+/, lookbehind: !0 },
  //     string: /(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\((?:\\.|[^)\\])*\))$/,
  //     punctuation: /^[\[\]!:]|[<>]/,
  //   },
  //   alias: 'url',
  // },
  bold: {
    pattern: /(^|[^\\])(\*\*|__)(?:(?:\r?\n|\r)(?!\r?\n|\r)|.)+?\2/,
    lookbehind: !0,
    inside: { punctuation: /^\*\*|^__|\*\*$|__$/ },
  },
  italic: {
    pattern: /(^|[^\\])([*_])(?:(?:\r?\n|\r)(?!\r?\n|\r)|.)+?\2/,
    lookbehind: !0,
    inside: { punctuation: /^[*_]|[*_]$/ },
  },
  // title
  timecoderow: topLevelMatchers.map((m) => ({
    pattern: new RegExp(m, ""),
    alias: "timecoderow",
  })),
  // blue
  timecode: [...inlineMatchers, ...dualInlineMatchers].map((m) => ({
    pattern: new RegExp(m, ""),
    alias: "timecode",
  })),
  // pink
  timecodesimple: [...nakedTimecodeMatchers, ...dualNakedTimecodeMatchers].map((m) => ({
    pattern: new RegExp(m, ""),
    alias: "timecodesimple",
  })),
  url: {
    pattern: /!?\[[^\]]+](?:\([^\s)]+(?:[\t ]+"(?:\\.|[^"\\])*")?\)| ?\[[^\]\n]*])/,
    inside: {
      variable: { pattern: /(!?\[)[^\]]+(?=]$)/, lookbehind: !0 },
      string: { pattern: /"(?:\\.|[^"\\])*"(?=\)$)/ },
    },
  },
});
(Prism.languages.markdown.bold as any).inside.url = Prism.util.clone(Prism.languages.markdown.url);
(Prism.languages.markdown.italic as any).inside.url = Prism.util.clone(Prism.languages.markdown.url);
(Prism.languages.markdown.bold as any).inside.italic = Prism.util.clone(Prism.languages.markdown.italic);
(Prism.languages.markdown.italic as any).inside.bold = Prism.util.clone(Prism.languages.markdown.bold);

const HOTKEYS = {
  "mod+j": "time",
  "shift+mod+j": "times",
};

const isTitleActive = (editor) => {
  const [title] = Editor.nodes(editor, {
    match: (n: any) => n.text && n.text.startsWith("#"),
  });

  return !!title;
};

const isSynopsisActive = (editor) => {
  const [synopsis] = Editor.nodes(editor, {
    match: (n: any) => n.text && n.text.startsWith(">"),
  });

  return !!synopsis;
};

const timecodeInterval = 4; // Interval between timecodes upon multi-timecode insertion (seconds)
// What does this even mean?
const subSecond = false; // Add sub-second timecodes
// Do... we care?

type Token = { content: string | Token[]; type: string } | string;

interface UrlParams {
  index_id: string;
}

const Notes = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { index_id: indexId } = useParams<keyof UrlParams>();
  const { progressSeconds: progress } = useSelector((s: RootState) => s.storyAuthoring);
  const item = useSelector((s: RootState) => s.storyAuthoring.items.find((note) => note._id === indexId));
  const { notes, unsaved, loaded } = item ?? {};
  const triggerAddBookmark = useSelector((s: RootState) => s.storyAuthoring.addBookmarkToNotes);
  const triggerAddChapter = useSelector((s: RootState) => s.storyAuthoring.addChapterToNotes);

  const [, setIsTitle] = useState(false);
  const [, setIsSynopsis] = useState(false);

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(() => withReact(withHistory(createEditor())), []);
  (window as any).editor = editor;

  useEffect(() => {
    if (unsaved) {
      setTimeout(() => {
        ReactEditor.focus(editor);
        Transforms.move(editor, { distance: 2, unit: "line" });
      });
    }
  }, []);

  // Triggered when "AddBookmark" button is clicked
  useEffect(() => {
    if (triggerAddBookmark) {
      addBookmark();
      dispatch(ToggleAddBookmarkToNotes(false));
    }
  }, [triggerAddBookmark]);

  // Triggered when "Create Chapter" button is clicked
  useEffect(() => {
    if (triggerAddChapter) {
      addChapter(triggerAddChapter);
      dispatch(ToggleAddChapterToNotes(null));
    }
  }, [triggerAddChapter]);

  const setNotes = useCallback(
    (indexId_: string, notes_: SlateParagraph[]) =>
      dispatch(
        SetIndexNotes({
          indexId: indexId_,
          notes: notes_,
        })
      ),
    [dispatch]
  );
  const seekTo = useCallback((time) => dispatch(SetSkipTime(time)), [dispatch]);

  const handleClick = useCallback(
    (event) => {
      const target = event.nativeEvent.srcElement;
      if (target.nodeName === "SPAN") {
        const text = target.innerText.trim();
        const timecodes = [
          ...text.matchAll(new RegExp(/\[(?:[01]\d|2[0123]):[012345]\d:[012345]\d]/g)),
          ...text.matchAll(new RegExp(/\[\d:[012345]\d:[012345]\d]/g)),
          ...text.matchAll(new RegExp(/\[[012345]\d:[012345]\d]/g)),
          ...text.matchAll(new RegExp(/\[\d:[012345]\d]/g)),
          ...text.matchAll(new RegExp(/(?:[01]\d|2[0123]):[012345]\d:[012345]\d/g)),
          ...text.matchAll(new RegExp(/\d:[012345]\d:[012345]\d/g)),
          ...text.matchAll(new RegExp(/[012345]\d:[012345]\d/g)),
          ...text.matchAll(new RegExp(/\d:[012345]\d/g)),
        ];

        if (timecodes.length === 0) return;

        let [ss, mm, hh = "00"] = timecodes[0][0].replace(/[[\]]/g, "").split(":").reverse();
        if (hh.length === 1) hh = `0${hh}`;
        if (mm.length === 1) mm = `0${mm}`;

        let tc = null;
        try {
          tc = timecode(`${hh}:${mm}:${ss}:00`, 60);
        } catch (ignored) {}

        if (tc) {
          seekTo(tc.frameCount / 60);
        }
      }
    },
    [seekTo]
  );

  const handleChange = useCallback(
    (newNotes) => {
      setIsTitle(isTitleActive(editor));
      setIsSynopsis(isSynopsisActive(editor));

      setNotes(indexId, newNotes);
    },
    [indexId, setNotes, editor, setIsTitle, setIsSynopsis]
  );

  const currentTimeCode = () => {
    const tc = timecode(progress * 60, 60);
    let [hh, mm, ss, mmm] = tc.toString().split(":");

    if (mmm.length === 1) mmm = `00${mmm}`;
    if (mmm.length === 2) mmm = `0${mmm}`;

    return subSecond ? `[${hh}:${mm}:${ss}.${mmm}]` : `[${hh}:${mm}:${ss}]`;
  };

  const addBookmark = () => {
    // Uncomment ths if you want to add at the beginning
    // Transforms.select(editor, { offset: 0, path: [0, 0] });
    const time = currentTimeCode();

    editor.insertBreak();
    editor.insertText(time);
    editor.insertBreak();
    editor.insertText(`# untitled`);
    editor.insertBreak();
  };

  const addChapter = ({ title, description, keywords }: IndexChapter) => {
    const time = currentTimeCode();

    editor.insertBreak();
    editor.insertText(time);
    editor.insertBreak();
    editor.insertText(`# ${title}`);
    if (description?.length > 0) {
      editor.insertBreak();
      editor.insertText(`> ${description}`);
    }
    if (keywords?.length > 0) {
      editor.insertBreak();
      editor.insertBreak();
      editor.insertText(`**${keywords}**`);
    }
    editor.insertBreak();
  };

  const onKeyDown = useCallback(
    (event) => {
      let once = false;
      Object.keys(HOTKEYS).forEach((hotkey) => {
        if (!once && isHotkey(hotkey, event)) {
          once = true;
          event.preventDefault();

          const mark = HOTKEYS[hotkey];

          if (mark.startsWith("time")) {
            const { selection } = editor;

            if (Range.isCollapsed(selection)) {
              const [
                {
                  offset,
                  path: [index],
                },
              ] = Range.edges(selection);

              const {
                children: [{ text }],
              } = notes[index];

              const tokenStart = offset - text.slice(0, offset).split(" ").pop().length;

              const token: string =
                text.substring(0, offset).split(" ").pop() + text.substring(offset).split(" ").reverse().pop();

              const matches = [
                ...token.matchAll(new RegExp(/\[(?:[01]\d|2[0123]):[012345]\d:[012345]\d]/g)),
                ...token.matchAll(new RegExp(/\[\d:[012345]\d:[012345]\d]/g)),
                ...token.matchAll(new RegExp(/\[[012345]\d:[012345]\d]/g)),
                ...token.matchAll(new RegExp(/\[\d:[012345]\d]/g)),
              ];

              if (matches.length > 0) {
                const notes2 = [...notes];

                notes2[index] = {
                  children: [
                    {
                      text: `${text.slice(0, tokenStart)}${token.replace(/[[\]]/g, "")}${text.slice(
                        tokenStart + token.length
                      )}`,
                    },
                  ],
                };

                Transforms.move(editor, { distance: 2, unit: "character", reverse: true });
                setNotes(indexId, notes2);
                return;
              }

              const matches2 = [
                ...token.matchAll(new RegExp(/(?:[01]\d|2[0123]):[012345]\d:[012345]\d/g)),
                ...token.matchAll(new RegExp(/\d:[012345]\d:[012345]\d/g)),
                ...token.matchAll(new RegExp(/[012345]\d:[012345]\d/g)),
                ...token.matchAll(new RegExp(/\d:[012345]\d/g)),
              ];

              if (matches2.length > 0) {
                const notes2 = [...notes];

                notes2[index] = {
                  children: [
                    {
                      text: `${text.slice(0, tokenStart)}[${token}]${text.slice(tokenStart + token.length)}`,
                    },
                  ],
                };

                setTimeout(() => Transforms.move(editor, { distance: 2, unit: "character", reverse: false }), 1);
                setNotes(indexId, notes2);
                return;
              }
            }

            const tokens = [];

            if (mark === "times") {
              [3, 2, 1]
                .filter((delta) => progress >= delta * parseFloat(timecodeInterval as any))
                .forEach((delta) => {
                  const tc = timecode((progress - delta * parseFloat(timecodeInterval as any)) * 60, 60);
                  const [hh, mm, ss, mmm] = tc.toString().split(":");
                  tokens.push(subSecond ? `[${hh}:${mm}:${ss}.${mmm}]` : `[${hh}:${mm}:${ss}]`);
                });
            }

            const tc = timecode(progress * 60, 60);
            let [hh, mm, ss, mmm] = tc.toString().split(":");

            mmm = `${mmm}`;
            if (mmm.length === 1) mmm = `00${mmm}`;
            if (mmm.length === 2) mmm = `0${mmm}`;

            tokens.push(subSecond ? `[${hh}:${mm}:${ss}.${mmm}]` : `[${hh}:${mm}:${ss}]`);

            editor.insertText(tokens.join(" "));
          }
        }
      });
      dispatch(handleHotkey(event));
    },
    [editor, progress, timecodeInterval, subSecond, notes, setNotes, indexId, dispatch]
  );

  const decorate = useCallback(([node, path]: NodeEntry): Range[] => {
    if (!Text.isText(node)) {
      return [];
    }

    const getLength = (token: Token): number => {
      if (typeof token === "string") {
        return token.length;
      } else if (typeof token.content === "string") {
        return token.content.length;
      } else {
        return token.content.reduce((l: number, t) => l + getLength(t), 0);
      }
    };

    const tokens: Token[] = Prism.tokenize(node.text, Prism.languages.markdown) as any;
    let start = 0;

    return tokens
      .map((token: Token): Range | null => {
        const length = getLength(token);
        const end = start + length;

        if (typeof token !== "string") {
          const ret = {
            [token.type]: true,
            anchor: { path, offset: start },
            focus: { path, offset: end },
          };
          start = end;
          return ret;
        }

        start = end;
        return null;
      })
      .filter(Boolean);
  }, []);

  if (!notes) {
    return null;
  }
  return (
    <NotesWrapper onClick={handleClick}>
      <Slate key={`${indexId}_${loaded}`} editor={editor} value={notes} onChange={handleChange}>
        <Editable
          style={{ width: "100%" }}
          autoFocus
          placeholder="Write some markdown…"
          {...{ decorate, renderLeaf, onKeyDown }}
        />
      </Slate>
    </NotesWrapper>
  );
};

export default Notes;
